import type { KitLine } from '@/lib/supplies';
import axios from 'axios';
import type { PortalFeatureFlags } from '@/lib/portal-features';
import type { APP_FEATURES_SETTING, AppFeatureFlags } from '@/lib/app-features';
import { authorizationHeader } from './api';
import type { TemplateValues } from '@/lib/templates';

/**
 * The events slice of the V3 API, reached through portal-api's staff proxy.
 *
 * A thin typed layer over `/api/v3/*` rather than a client SDK: the
 * V3 API owns the shapes, and this file only names the parts the admin screens
 * actually read.
 */

/** A person as V3 returns them on events and searches. */
export interface EventPerson {
  readonly _id: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly avatar?: string;
  readonly role?: string;
}

/** A Salesforce/venue contact carried on the event (not a portal user). */
/** Where a recording link lives; hosts we embed get a player, anything else is a link. */
export const RECORDING_PROVIDER_LABELS = { fathom: 'Fathom', loom: 'Loom', google_drive: 'Google Drive', link: 'Link' } as const;
export type RecordingProvider = keyof typeof RECORDING_PROVIDER_LABELS;

/** A meeting recording on an event. Staff-only. */
export interface EventRecording {
  readonly _id: string;
  readonly provider: RecordingProvider;
  readonly url: string;
  /** The player URL when the link was a share link from a host we embed. */
  readonly embedUrl?: string | null;
  readonly title: string;
  readonly recordedAt?: string;
  readonly viewableBy: readonly ('internal' | 'contractor')[];
  readonly createdAt?: string;
}

/** A hand-added contact on an event. */
export interface LocalContact {
  readonly _id: string;
  readonly typeKey: string;
  readonly name: string;
  readonly email?: string;
  readonly phone?: string;
  readonly cell?: string;
  readonly company?: string;
  readonly note?: string;
  readonly salesforceContactId?: string;
  readonly createdAt?: string;
}

/** What the contact form sends. */
export type LocalContactWrite = Omit<LocalContact, '_id' | 'createdAt'>;

export interface EventContact {
  readonly name?: string;
  readonly phone?: string;
  readonly cell?: string;
  readonly email?: string;
  readonly role?: string;
  readonly isPrimary?: boolean;
}

/** A venue contact block. */
export interface VenueContact {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phone?: string;
}

/** A venue on the event. */
export interface EventVenue {
  readonly name?: string;
  readonly street?: string;
  readonly city?: string;
  readonly state?: string;
  readonly postalCode?: string;
  readonly room?: string;
  /** The venue's zone, geocoded on sync — `id` is IANA, `name` is human. */
  readonly timezone?: { readonly id?: string; readonly name?: string };
}

/** One event in the list. Flattened columns come from the V3 list endpoint. */
export interface EventSummary {
  readonly _id: string;
  readonly programDisplayName?: string;
  readonly programName?: string;
  readonly contractNumber?: string;
  /** How many events share this contract number's base (1 = stands alone). */
  readonly familySize?: number;
  readonly stageName?: string;
  /** Mirrored early (Contract Questions) and not yet signed. */
  readonly awaitingContract?: boolean;
  /**
   * Legacy date field. Prefer the helpers in `@/lib/event-time` over reading
   * this directly — it has drifted from the event's real day, and it carries
   * no zone, so formatting it in the browser shifts the date for some readers.
   */
  readonly eventDate?: string;
  /** The event's true start/end instants, in UTC. The day comes from these. */
  readonly timeStart?: string;
  readonly timeEnd?: string;
  /** Wall-clock times at the venue, e.g. "3:00pm" — or "TBD". */
  readonly timeStartString?: string;
  readonly timeEndString?: string;
  /** Short zone label ("EDT") and its IANA name ("America/New_York"). */
  readonly timeZone?: string;
  readonly longTimeZone?: string;
  readonly participants?: number;
  readonly accountName?: string;
  readonly accountSalesforceId?: string | null;
  readonly location?: string;
  readonly startVenue?: EventVenue;
  readonly leadFacilitatorName?: string;
  readonly leadFacilitatorId?: string | null;
}

/** The full event, as the detail screen reads it. */
/** A portal booking request, as the event overview and the link picker show it. */
export interface BookingRequestSummary {
  readonly id: string;
  readonly reference: string;
  readonly programName: string;
  readonly status: string;
  readonly organizationName: string | null;
  readonly preferredDate: string | null;
}

export interface EventDetail extends EventSummary {
  /** The portal booking this event delivers; null when it delivers none. Staff only. */
  readonly bookingRequest?: BookingRequestSummary | null;
  /** True when the event delivers a portal booking, so the recap offers "Feedback for the client". */
  readonly offersClientFeedback?: boolean;
  readonly opportunityName?: string;
  readonly activity?: string;
  /** Extra participant breakdown, e.g. "13 teams, 26 bikes". */
  readonly afterParticipants?: string;
  readonly account?: { readonly name?: string; readonly type?: string; readonly salesforceId?: string };
  readonly charity?: readonly {
    readonly name?: string;
    readonly type?: string;
    readonly charityRep?: string;
    readonly deliveryContact?: string;
  }[];
  /** The event's primary Salesforce contact. */
  readonly contact?: EventContact;
  /** Every Salesforce contact on the opportunity, with their role. */
  readonly contacts?: readonly EventContact[];
  /** The charity plan (directory charity, outcome, speaker, pickup). */
  readonly charityPlan?: {
    readonly charityId?: string;
    readonly outcome?: 'pending' | 'accepted' | 'declined' | 'no_response' | 'client_chose';
    readonly speaker?: { readonly name?: string; readonly cell?: string };
    readonly arrivalAt?: string;
    readonly pickupPlan?: string;
    readonly talkingPoints?: string;
    readonly note?: string;
    readonly updatedAt?: string;
  };
  /** Meeting recordings the viewer may see (never returned to clients). */
  readonly recordings?: readonly EventRecording[];
  /** Contacts the team added by hand, typed; never part of contactEmails. */
  readonly localContacts?: readonly LocalContact[];
  /** The addresses that can see this event on My Events (normalized lowercase). */
  readonly contactEmails?: readonly string[];
  /** Rollout switch (Salesforce MyEvents_Contracting__c): contracts and
   * payments run through My Events only when true. */
  readonly myEventsContracting?: boolean;
  /** The catalog program's Mongo id, when the synced program exists. */
  readonly programId?: string;
  /** The Salesforce opportunity id, for the View-in-Salesforce link. */
  readonly opportunityId?: string;
  /** Neighbouring events in date order, role-scoped. */
  readonly previousEventId?: string | null;
  readonly nextEventId?: string | null;
  readonly startVenue?: EventVenue;
  readonly endVenue?: EventVenue;
  readonly venueContact?: VenueContact;
  readonly endingVenueContact?: VenueContact;
  readonly leadFacilitator?: EventPerson | string | null;
  readonly coFacilitator?: EventPerson | string | null;
  readonly salesperson?: EventPerson | string | null;
  readonly programManager?: EventPerson | string | null;
  readonly contractManager?: EventPerson | string | null;
  readonly accountingContact?: EventPerson | string | null;
  readonly productionContact?: EventPerson | string | null;
  readonly users?: readonly EventPerson[];
}

/**
 * Builds a person's display name, falling back to their email.
 *
 * @param person - The person.
 * @returns Something always renderable.
 */
export function personName(person: EventPerson): string {
  return [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email || 'Unknown';
}

/** Which slice of the calendar the list shows. */
export type EventState = 'upcoming' | 'past' | 'all';

/** Rows per page on the events list. */
export const EVENTS_PAGE_SIZE = 25;

/**
 * Fetches the event list.
 *
 * @param options - State filter, search text and page.
 * @param signal - Abort signal from React Query.
 * @returns Events plus pagination metadata.
 */
export async function listEvents(
  options: {
    readonly state: EventState;
    readonly search: string;
    readonly page: number;
    readonly sort?: { readonly key: string; readonly direction: 'asc' | 'desc' };
    /** Awaiting-contract events are hidden unless 'include' (or 'only'). */
    readonly awaitingContract?: 'include' | 'only';
    /** One contract family (the shared numeric base). */
    readonly family?: string;
  },
  signal?: AbortSignal,
): Promise<{
  readonly events: readonly EventSummary[];
  readonly total: number;
  readonly totalPages: number;
  readonly limit: number;
}> {
  const params: Record<string, string | number> = { page: options.page, limit: EVENTS_PAGE_SIZE };
  if (options.state !== 'all') params['state'] = options.state;
  if (options.search.trim()) params['search'] = options.search.trim();
  if (options.sort) {
    params['sortField'] = options.sort.key;
    params['sortDirection'] = options.sort.direction;
  }
  if (options.awaitingContract) params['awaitingContract'] = options.awaitingContract;
  if (options.family) params['family'] = options.family;

  const response = await axios.get<{
    data: EventSummary[];
    pagination?: { total?: number; totalPages?: number };
  }>('/api/v3/events', { params, ...(signal ? { signal } : {}), headers: await authHeader() });
  const total = response.data.pagination?.total ?? response.data.data.length;
  return {
    events: response.data.data,
    total,
    totalPages: response.data.pagination?.totalPages ?? Math.max(1, Math.ceil(total / EVENTS_PAGE_SIZE)),
    limit: EVENTS_PAGE_SIZE,
  };
}

// ─── Shipping (venue receiving profile) ─────────────────────────────────────

/** Where materials go. Mirrors the API's SHIPPING_MODE. */
export const SHIPPING_MODE = {
  venue: 'venue',
  clientOffice: 'client_office',
  facilitator: 'facilitator',
  other: 'other',
  none: 'none',
} as const;
export type ShippingMode = (typeof SHIPPING_MODE)[keyof typeof SHIPPING_MODE];
export const SHIPPING_MODE_LABELS: Readonly<Record<ShippingMode, string>> = {
  venue: 'Ship to the venue',
  client_office: "Ship to the client's office",
  facilitator: 'Ship to the facilitator',
  other: 'Ship somewhere else',
  none: 'No shipping',
};

export const INDOOR_OUTDOOR = { indoor: 'indoor', outdoor: 'outdoor', unknown: 'unknown' } as const;
export type IndoorOutdoor = (typeof INDOOR_OUTDOOR)[keyof typeof INDOOR_OUTDOOR];

export interface ShipToAddress {
  readonly name?: string;
  readonly attn?: string;
  readonly street?: string;
  readonly city?: string;
  readonly state?: string;
  readonly postalCode?: string;
  readonly phone?: string;
}

/** The stored receiving profile. Dates arrive as ISO strings. */
export interface ShippingProfile {
  readonly mode?: ShippingMode;
  readonly shipTo?: ShipToAddress;
  readonly holdFor?: string;
  readonly deliveryWindow?: { readonly start?: string; readonly end?: string };
  readonly palletsOk?: boolean;
  readonly liftGate?: boolean;
  readonly insideDelivery?: boolean;
  readonly receivingHours?: string;
  readonly returnInstructions?: string;
  readonly returnPickup?: { readonly carrier?: string; readonly date?: string };
  readonly indoorOutdoor?: IndoorOutdoor;
  readonly confirmed?: { readonly by?: string; readonly at?: string; readonly with?: string };
  readonly notes?: string;
  /** Every "materials shipped" email sent to the client, oldest first. */
  readonly clientEmails?: readonly { readonly sentAt?: string; readonly to?: readonly string[] }[];
}

/** What the API returns: the profile plus what it derives. */
export interface ShippingView {
  readonly profile: ShippingProfile;
  readonly defaultShipTo?: ShipToAddress;
  readonly labelLines: readonly string[];
  readonly confirmed: boolean;
}

/** The PUT body: the whole profile; `confirmed.with` names the venue person. */
export interface ShippingPatch {
  readonly mode?: ShippingMode;
  readonly shipTo?: ShipToAddress | null;
  readonly holdFor?: string;
  readonly deliveryWindow?: { readonly start?: string | null; readonly end?: string | null } | null;
  readonly palletsOk?: boolean | null;
  readonly liftGate?: boolean | null;
  readonly insideDelivery?: boolean | null;
  readonly receivingHours?: string;
  readonly returnInstructions?: string;
  readonly returnPickup?: { readonly carrier?: string; readonly date?: string | null } | null;
  readonly indoorOutdoor?: IndoorOutdoor;
  readonly confirmed?: { readonly with?: string } | null;
  readonly notes?: string;
}

/** Reads an event's receiving profile. */
export async function readEventShipping(eventId: string, signal?: AbortSignal): Promise<ShippingView> {
  const response = await axios.get<{ data: ShippingView }>(`/api/v3/events/${eventId}/shipping`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/** Replaces an event's receiving profile. Internal roles only. */
export async function saveEventShipping(eventId: string, body: ShippingPatch): Promise<ShippingView> {
  const response = await axios.put<{ data: ShippingView }>(`/api/v3/events/${eventId}/shipping`, body, {
    headers: await authHeader(),
  });
  return response.data.data;
}

/** One event booked under the same contract number, as the family panel lists it. */
export interface FamilyMember {
  readonly _id: string;
  readonly contractNumber?: string;
  readonly site: string | null;
  readonly amendment: number | null;
  readonly eventDate?: string;
  readonly timeStartString?: string;
  readonly timeEndString?: string;
  readonly timeZone?: string;
  readonly city?: string;
  readonly state?: string;
  readonly participants?: number;
  readonly stageName?: string;
  readonly leadFacilitatorName: string;
  readonly leadFacilitatorId: string | null;
  readonly programName?: string;
}

/**
 * The events sharing this event's contract number, including itself, in
 * the sheet's order (parent, then sites A, B, C…).
 *
 * @param eventId - Mongo id.
 * @param signal - Abort signal.
 * @returns The family members; one entry when the event stands alone.
 */
export async function readEventFamily(eventId: string, signal?: AbortSignal): Promise<readonly FamilyMember[]> {
  const response = await axios.get<{ data: FamilyMember[] }>(`/api/v3/events/${eventId}/family`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/**
 * Fetches one event.
 *
 * @param eventId - Mongo id.
 * @param signal - Abort signal.
 * @returns The event.
 */
/**
 * Re-imports the event's data from its Salesforce opportunity.
 *
 * @param eventId - Mongo id.
 * @returns Whether anything synced.
 */
export async function syncEventFromSalesforce(eventId: string): Promise<{ synced: boolean }> {
  const response = await axios.post<{ data: { synced: boolean } }>(
    `/api/v3/events/${eventId}/sync`,
    {},
    { headers: await authHeader() },
  );
  return response.data.data;
}

/** Result of the staging-only Salesforce opportunity import. */
export interface OpportunityImportResult {
  readonly status: 'created' | 'updated' | 'skipped';
  readonly reason?: string;
  readonly eventId?: string;
  readonly opportunityId: string;
  readonly opportunityName: string | null;
  readonly contractNumber: string | null;
}

/**
 * Imports one Salesforce opportunity into the events collection by
 * Opportunity Id or contract number — the staging tool behind
 * `FEATURE_SF_OPPORTUNITY_IMPORT`. The endpoint does not exist where the
 * flag is off (404).
 *
 * @param reference - Opportunity Id (006…) or contract number (9363-BC).
 * @returns What the import did.
 */
export async function importSalesforceOpportunity(
  reference: string,
): Promise<OpportunityImportResult> {
  const response = await axios.post<{ data: OpportunityImportResult }>(
    '/api/v3/events/import-opportunity',
    { reference },
    { headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Whether the staging opportunity-import tool is enabled in this
 * environment, per the API's public config.
 *
 * @param signal - Abort signal.
 * @returns True when the import card should render.
 */
export async function readSfOpportunityImportEnabled(signal?: AbortSignal): Promise<boolean> {
  const response = await axios.get<{ data: { featureSfOpportunityImport?: boolean } }>(
    '/api/v3/public-config',
    signal ? { signal } : {},
  );
  return response.data.data.featureSfOpportunityImport === true;
}

/**
 * Portal bookings across every client that match a reference or program —
 * the picker for linking an event to the request it delivers.
 *
 * @param term - What was typed.
 * @param signal - Abort signal.
 * @returns Up to twenty bookings, newest first.
 */
export async function searchBookingRequests(
  term: string,
  signal?: AbortSignal,
): Promise<readonly BookingRequestSummary[]> {
  const response = await axios.get<{ data: BookingRequestSummary[] }>('/api/v3/events/booking-requests', {
    params: { q: term },
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/**
 * Records which portal booking an event delivers, or clears it.
 *
 * @param eventId - Mongo id.
 * @param bookingRequestId - The booking, or null to unlink.
 * @returns The booking now on the event.
 */
/** Who last wrote one of the CSR plan's post-event fields. */
export interface CsrAttribution {
  readonly name: string;
  readonly side: 'CLIENT' | 'BCE';
  readonly at: string;
}

/** A §40 catalog kind a donated line can count toward. */
export type DonationKind = 'bicycles' | 'guitars' | 'stemKits' | 'carePackages' | 'foodPounds' | 'toys';

/** One line on the list of what was donated. */
export interface DonationItem {
  readonly id: string;
  readonly description: string;
  readonly quantity: number;
  /** Absent for a line that shows to the client but adds to no catalog total. */
  readonly kind?: DonationKind;
}

/** A new line, or the fields of one being changed. A null kind clears it. */
export interface DonationItemInput {
  readonly description: string;
  readonly quantity: number;
  readonly kind: DonationKind | null;
}

/** The CSR plan on the client's workspace for this event (§37–38). */
export interface EventCsrPlan {
  readonly beneficiaryName?: string | null;
  readonly cause?: string | null;
  readonly charityContactName?: string | null;
  readonly charityContactEmail?: string | null;
  readonly donationRequirements?: string | null;
  readonly communitiesSupported?: string | null;
  /** The totals of `donationItems` per catalog kind; derived by the API. */
  readonly donationCounts?: Readonly<Record<string, number>> | null;
  /** What was donated, line by line. */
  readonly donationItems: readonly DonationItem[];
  readonly donationStatus?: string | null;
  readonly transportResponsibility?: string | null;
  readonly transportPlan?: string | null;
  readonly charityArrivalTime?: string | null;
  readonly vehicleAccessNotes?: string | null;
  readonly alternativeTransportNote?: string | null;
  readonly engagedMinutes?: number | null;
  readonly beneficiaryAcknowledgement?: string | null;
  readonly thankYouMessage?: string | null;
  readonly documentationNote?: string | null;
  readonly attributions?: Readonly<Record<string, CsrAttribution>> | null;
  readonly updatedByName?: string | null;
  readonly updatedAt?: string;
}

/** The CSR side of an event as the admin app reads it. */
export interface EventCsr {
  /** The portal organization the event's account belongs to; null when not linked. */
  readonly organization: { readonly id: string; readonly name: string; readonly slug: string } | null;
  /** The charity Salesforce filed on the event. */
  readonly charity: readonly {
    readonly name: string | null;
    readonly type: string | null;
    readonly charityRep: string | null;
    readonly deliveryContact: string | null;
  }[];
  readonly plan: EventCsrPlan | null;
}

export type EventCsrChanges = Partial<{
  beneficiaryName: string;
  cause: string;
  charityContactName: string;
  charityContactEmail: string;
  donationRequirements: string;
  communitiesSupported: string;
  donationStatus: string;
  transportResponsibility: string;
  transportPlan: string;
  charityArrivalTime: string;
  vehicleAccessNotes: string;
  alternativeTransportNote: string;
  engagedMinutes: number;
  beneficiaryAcknowledgement: string;
  thankYouMessage: string;
  documentationNote: string;
}>;

export async function readEventCsr(eventId: string, signal?: AbortSignal): Promise<EventCsr> {
  const response = await axios.get<{ data: EventCsr }>(`/api/v3/events/${eventId}/csr`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

export async function saveEventCsr(eventId: string, changes: EventCsrChanges): Promise<EventCsrPlan> {
  const response = await axios.patch<{ data: EventCsrPlan }>(`/api/v3/events/${eventId}/csr`, changes, {
    headers: await authHeader(),
  });
  return response.data.data;
}

/**
 * Adds a line to the list of what was donated.
 *
 * @param eventId - Mongo id.
 * @param item - The line; a null kind adds it without a catalog total.
 * @returns The plan, with the counters the list now adds up to.
 */
export async function addEventDonationItem(eventId: string, item: DonationItemInput): Promise<EventCsrPlan> {
  const { kind, ...rest } = item;
  const response = await axios.post<{ data: EventCsrPlan }>(
    `/api/v3/events/${eventId}/csr/donation-items`,
    kind === null ? rest : { ...rest, kind },
    { headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Changes one donated line.
 *
 * @param eventId - Mongo id.
 * @param itemId - The line.
 * @param item - Its new fields.
 * @returns The plan.
 */
export async function updateEventDonationItem(
  eventId: string,
  itemId: string,
  item: DonationItemInput,
): Promise<EventCsrPlan> {
  const response = await axios.patch<{ data: EventCsrPlan }>(
    `/api/v3/events/${eventId}/csr/donation-items/${encodeURIComponent(itemId)}`,
    item,
    { headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Takes a line off the list.
 *
 * @param eventId - Mongo id.
 * @param itemId - The line.
 * @returns The plan.
 */
export async function removeEventDonationItem(eventId: string, itemId: string): Promise<EventCsrPlan> {
  const response = await axios.delete<{ data: EventCsrPlan }>(
    `/api/v3/events/${eventId}/csr/donation-items/${encodeURIComponent(itemId)}`,
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function linkEventBooking(
  eventId: string,
  bookingRequestId: string | null,
): Promise<BookingRequestSummary | null> {
  const response = await axios.patch<{ data: { bookingRequest: BookingRequestSummary | null } }>(
    `/api/v3/events/${eventId}/booking-link`,
    { bookingRequestId },
    { headers: await authHeader() },
  );
  return response.data.data.bookingRequest;
}

export async function readEvent(eventId: string, signal?: AbortSignal): Promise<EventDetail> {
  const response = await axios.get<{ data: EventDetail }>(`/api/v3/events/${eventId}`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/**
 * People assigned to an event.
 *
 * @param eventId - Mongo id.
 * @param signal - Abort signal.
 * @returns The assigned people.
 */
export async function listEventUsers(
  eventId: string,
  signal?: AbortSignal,
): Promise<readonly EventPerson[]> {
  const response = await axios.get<{ data: EventPerson[] }>(
    `/api/v3/events/${eventId}/users`,
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Searches people who could be assigned.
 *
 * @param search - Name or email fragment.
 * @param signal - Abort signal.
 * @returns Up to 20 matches.
 */
export async function searchUsers(
  search: string,
  signal?: AbortSignal,
  /** Limit to these account roles; omitted searches everyone. */
  roles?: readonly string[],
): Promise<readonly EventPerson[]> {
  const response = await axios.get<{ data: EventPerson[] }>('/api/v3/users/search', {
    params: { search, ...(roles && roles.length > 0 ? { roles: roles.join(',') } : {}) },
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/** One directory row, with the company the search endpoint omits. */
export interface DirectoryPerson extends EventPerson {
  readonly company?: string;
  readonly avatar?: string;
}

/** A full person profile, as the profile page reads it. */
export interface PersonProfile extends DirectoryPerson {
  readonly phone?: string;
  /** What they do at the company; signs the emails they send. */
  readonly jobTitle?: string;
  readonly accountType?: string;
  readonly address?: {
    readonly street?: string;
    readonly city?: string;
    readonly state?: string;
    readonly postalCode?: string;
  };
  readonly associatedEmails?: readonly string[];
  /** Their own call-booking links, offered to website leads they own. */
  readonly schedulingLinks?: readonly SchedulingLink[];
  readonly createdAt?: string;
}

/** The call lengths a staff member can publish a link for, in minutes. Mirrors the API. */
export const MEETING_DURATIONS = [10, 15] as const;
export type MeetingDuration = (typeof MEETING_DURATIONS)[number];

/** Hosts a scheduling link may point at. Mirrors the API's `SCHEDULING_LINK_HOSTS`. */
export const SCHEDULING_LINK_HOSTS: ReadonlySet<string> = new Set(['calendly.com']);

/** One published call-booking link on a profile. */
export interface SchedulingLink {
  readonly durationMinutes: MeetingDuration;
  readonly url: string;
}

/**
 * True for an https link on an allowlisted scheduler host, with no
 * credentials or port. The API enforces the same rule; this only lets the
 * form say so before saving.
 */
export function isAllowedSchedulingUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  return (
    url.protocol === 'https:' &&
    SCHEDULING_LINK_HOSTS.has(url.hostname.toLowerCase()) &&
    url.username === '' &&
    url.password === '' &&
    url.port === ''
  );
}

/**
 * Reads one person's full profile.
 *
 * @param userId - Mongo id.
 * @param signal - Abort signal from React Query.
 * @returns The profile.
 */
export async function readUser(userId: string, signal?: AbortSignal): Promise<PersonProfile> {
  const response = await axios.get<{ data: PersonProfile }>(`/api/v3/users/${userId}`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/** Page metadata returned alongside a directory page. */
export interface DirectoryPage {
  readonly people: readonly DirectoryPerson[];
  readonly page: number;
  readonly totalPages: number;
  readonly total: number;
}

/**
 * The paginated directory — every reachable person, newest-name first,
 * narrowed by an optional search. Unlike {@link searchUsers} (capped at 20
 * for the event-assignment picker), this is the browsable list the Directory
 * shows by default.
 *
 * @param params - Page number and optional search text.
 * @param signal - Abort signal from React Query.
 * @returns One page of people plus pagination.
 */
export async function listUsers(
  params: { page: number; search?: string; role?: string; roleId?: string },
  signal?: AbortSignal,
): Promise<DirectoryPage> {
  const response = await axios.get<{
    data: DirectoryPerson[];
    pagination: { page: number; totalPages: number; total: number };
  }>('/api/v3/users', {
    params: {
      page: params.page,
      limit: 25,
      ...(params.search ? { search: params.search } : {}),
      ...(params.role ? { role: params.role } : {}),
      ...(params.roleId ? { roleId: params.roleId } : {}),
    },
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return {
    people: response.data.data,
    page: response.data.pagination.page,
    totalPages: response.data.pagination.totalPages,
    total: response.data.pagination.total,
  };
}

/**
 * Assigns people to an event.
 *
 * @param eventId - Mongo id.
 * @param userIds - People to add.
 */
export async function addEventUsers(eventId: string, userIds: readonly string[]): Promise<void> {
  await axios.post(
    `/api/v3/events/${eventId}/users`,
    { userIds },
    { headers: await authHeader() },
  );
}

/**
 * Removes people from an event.
 *
 * @param eventId - Mongo id.
 * @param userIds - People to remove.
 */
export async function removeEventUsers(eventId: string, userIds: readonly string[]): Promise<void> {
  await axios.delete(`/api/v3/events/${eventId}/users`, {
    data: { userIds },
    headers: await authHeader(),
  });
}

/**
 * Builds the auth header from the same token source the admin API client uses,
 * so proxy calls carry the same session with no second plumbing path.
 *
 * @returns Headers for a staff-proxied request.
 */
export async function authHeader(): Promise<Record<string, string>> {
  return authorizationHeader();
}

// ─── Notes ──────────────────────────────────────────────────────────────────

/** The note categories V3 accepts, from the legacy model's enum. */
/**
 * Note categories in display order. 'Facilitator' sits last on purpose
 * (production asked for planning notes at the bottom); 'Activity' holds game
 * links and supplemental activity info.
 */
export const NOTE_CATEGORIES = [
  'Basic',
  'Activity',
  'Assistants',
  'Car Rental',
  'Charity',
  'Closing/Recap',
  'Expenses',
  'Flight',
  'Hotel',
  'Production',
  'Supplies',
  'Time',
  'Travel',
  'Facilitator',
] as const;

export type NoteCategory = (typeof NOTE_CATEGORIES)[number];

/**
 * Display labels where they differ from the stored value. 'Facilitator'
 * reads as "Planning" in the new app; the stored value is unchanged so
 * legacy keeps grouping these notes correctly.
 */
const NOTE_CATEGORY_LABELS: Partial<Record<NoteCategory, string>> = {
  Facilitator: 'Planning',
};

/** The display label for a note category. */
export function noteCategoryLabel(category: string): string {
  return NOTE_CATEGORY_LABELS[category as NoteCategory] ?? category;
}

/** One note on an event. */
export interface EventNote {
  readonly _id: string;
  readonly category?: string;
  readonly subject: string;
  readonly body: string;
  readonly owner?: EventPerson | string;
  readonly updatedBy?: EventPerson | string;
  readonly archivedBy?: EventPerson | string;
  readonly createdAt?: string;
  readonly updatedAt?: string;
  readonly archivedAt?: string;
  readonly pinnedAt?: string;
  /** Structured notes: the template followed, its status and field values. */
  readonly templateKey?: string;
  readonly templateVersion?: number;
  readonly status?: string;
  readonly values?: TemplateValues;
}

/** What the composer sends: category, subject, body and the structured part. */
export interface NoteWrite {
  readonly category: NoteCategory;
  readonly subject: string;
  readonly body: string;
  /** Null clears the structure; absent leaves it alone. */
  readonly templateKey?: string | null;
  readonly status?: string | null;
  readonly values?: TemplateValues | null;
}

/**
 * Notes on an event, newest first. Pass `showArchived` to include archived
 * notes (the response then carries both — filter by `archivedAt` client-side).
 *
 * @param eventId - Mongo id.
 * @param options - `showArchived` and/or a `category` filter.
 * @param signal - Abort signal.
 * @returns The notes.
 */
export async function listEventNotes(
  eventId: string,
  options: { showArchived?: boolean; category?: string } = {},
  signal?: AbortSignal,
): Promise<readonly EventNote[]> {
  const params: Record<string, string> = { limit: '200' };
  if (options.showArchived) params['showArchived'] = 'true';
  if (options.category) params['category'] = options.category;
  const response = await axios.get<{ data: EventNote[] }>(
    `/api/v3/events/${eventId}/notes`,
    { params, ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Creates a note on an event.
 *
 * @param eventId - Mongo id.
 * @param note - Category, subject and body.
 */
export async function createEventNote(eventId: string, note: NoteWrite): Promise<void> {
  await axios.post(`/api/v3/events/${eventId}/notes`, note, {
    headers: await authHeader(),
  });
}

/**
 * Updates a note's category, subject and body.
 *
 * @param noteId - Mongo id.
 * @param note - The updated fields.
 */
export async function updateEventNote(noteId: string, note: NoteWrite): Promise<void> {
  await axios.put(`/api/v3/notes/${noteId}`, note, { headers: await authHeader() });
}

/**
 * Archives a note — the soft delete V3 supports.
 *
 * @param noteId - Mongo id.
 */
export async function archiveNote(noteId: string): Promise<void> {
  await axios.post(`/api/v3/notes/${noteId}/archive`, {}, { headers: await authHeader() });
}

/**
 * Restores an archived note back into the event.
 *
 * @param noteId - Mongo id.
 */
export async function restoreNote(noteId: string): Promise<void> {
  await axios.post(`/api/v3/notes/${noteId}/restore`, {}, { headers: await authHeader() });
}

/**
 * Pins or unpins a note. Pinned notes surface first in their category.
 *
 * @param noteId - Mongo id.
 * @param pinned - True to pin, false to unpin.
 */
export async function setNotePinned(noteId: string, pinned: boolean): Promise<void> {
  await axios.post(
    `/api/v3/notes/${noteId}/${pinned ? 'pin' : 'unpin'}`,
    {},
    { headers: await authHeader() },
  );
}

// ─── Recordings ─────────────────────────────────────────────────────────────

/** Adds a recording link; returns the event's recordings. Internal roles. */
export async function createEventRecording(
  eventId: string,
  body: { readonly url: string; readonly title: string; readonly recordedAt?: string; readonly viewableBy: readonly ('internal' | 'contractor')[] },
): Promise<readonly EventRecording[]> {
  const response = await axios.post<{ data: EventRecording[] }>(`/api/v3/events/${eventId}/recordings`, body, { headers: await authHeader() });
  return response.data.data;
}

/** Removes a recording; returns the event's recordings. */
export async function deleteEventRecording(eventId: string, recordingId: string): Promise<readonly EventRecording[]> {
  const response = await axios.delete<{ data: EventRecording[] }>(`/api/v3/events/${eventId}/recordings/${recordingId}`, { headers: await authHeader() });
  return response.data.data;
}

// ─── Local contacts ─────────────────────────────────────────────────────────

/** Adds a hand-typed contact; returns the event's local contacts. */
export async function createEventContact(eventId: string, body: LocalContactWrite): Promise<readonly LocalContact[]> {
  const response = await axios.post<{ data: LocalContact[] }>(`/api/v3/events/${eventId}/contacts`, body, { headers: await authHeader() });
  return response.data.data;
}

/** Edits a hand-typed contact; returns the event's local contacts. */
export async function updateEventContact(eventId: string, contactId: string, body: Partial<LocalContactWrite>): Promise<readonly LocalContact[]> {
  const response = await axios.patch<{ data: LocalContact[] }>(`/api/v3/events/${eventId}/contacts/${contactId}`, body, { headers: await authHeader() });
  return response.data.data;
}

/** Removes a hand-typed contact; returns the event's local contacts. */
export async function deleteEventContact(eventId: string, contactId: string): Promise<readonly LocalContact[]> {
  const response = await axios.delete<{ data: LocalContact[] }>(`/api/v3/events/${eventId}/contacts/${contactId}`, { headers: await authHeader() });
  return response.data.data;
}

// ─── Summary tab ────────────────────────────────────────────────────────────

/** The free-text event summary. */
export interface EventSummaryText {
  readonly html?: string;
  readonly updatedAt?: string;
  readonly updatedBy?: EventPerson | string;
}

/** The latest structured note in one category. */
export interface PinnedSummary {
  readonly category: string;
  readonly note: EventNote;
}

export interface EventSummaryView {
  readonly summary: EventSummaryText;
  readonly pinned: readonly PinnedSummary[];
}

/** The Summary tab: free text plus the latest templated note per category. */
export async function readEventSummary(eventId: string, signal?: AbortSignal): Promise<EventSummaryView> {
  const response = await axios.get<{ data: EventSummaryView }>(`/api/v3/events/${eventId}/summary`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/** Saves the free-text summary. Internal roles only. */
export async function saveEventSummary(eventId: string, html: string): Promise<void> {
  await axios.put(`/api/v3/events/${eventId}/summary`, { html }, { headers: await authHeader() });
}

/** Asks the assistant for a draft summary. Nothing is saved. */
export async function draftEventSummary(eventId: string): Promise<string> {
  const response = await axios.post<{ data: { html: string } }>(`/api/v3/events/${eventId}/summary/draft`, {}, { headers: await authHeader() });
  return response.data.data.html;
}

// ─── Shipments ──────────────────────────────────────────────────────────────

/** A stored shipment address — Shippo writes street1/zip, legacy street. */
export interface ShipmentAddress {
  readonly name?: string;
  readonly company?: string;
  readonly street?: string;
  readonly street1?: string;
  readonly city?: string;
  readonly state?: string;
  readonly zip?: string;
  readonly country?: string;
}

/**
 * Tracking states a shipment can be in, keyed by the value Shippo stores.
 * Shipments on carriers Shippo cannot track have no status at all, so the
 * list's "All" option is the only way to reach those.
 */
export const SHIPMENT_STATUSES = {
  PRE_TRANSIT: 'Pre-transit',
  TRANSIT: 'In transit',
  DELIVERED: 'Delivered',
  RETURNED: 'Returned',
  FAILURE: 'Failure',
} as const;

/** A tracking state the list can filter by. */
export type ShipmentStatus = keyof typeof SHIPMENT_STATUSES;

/**
 * A tracking state's display name.
 *
 * @param status - The stored status value.
 * @returns The label, or the raw value for anything unrecognised.
 */
export function shipmentStatusLabel(status: string | undefined): string {
  if (!status) return 'Unknown';
  return SHIPMENT_STATUSES[status as ShipmentStatus] ?? status.replace(/_/g, ' ');
}

/**
 * Carriers staff book with, keyed by the value stored on the shipment.
 *
 * `fedex_freight` is a distinct stored value but tracks through Shippo's
 * `fedex` token, so it must stay selectable — dropping it from the list
 * silently rewrites those shipments to `fedex` on the next edit.
 */
export const SHIPMENT_CARRIERS = {
  fedex: 'FedEx',
  fedex_freight: 'FedEx Freight',
  ups: 'UPS',
  usps: 'USPS',
  dhl_express: 'DHL Express',
  dhl_ecommerce: 'DHL eCommerce',
  xpo: 'XPO Logistics',
  averitt: 'Averitt Express',
  saia: 'Saia',
  old_dominion: 'Old Dominion',
  estes: 'Estes',
  rl_carriers: 'R+L Carriers',
  abf: 'ABF Freight',
  southeastern: 'Southeastern Freight Lines',
  pilot: 'Pilot Freight',
  pitt_ohio: 'Pitt Ohio',
  other: 'Other',
} as const;

/**
 * LTL freight carriers: tracked by PRO number, and their status is set by
 * hand because Shippo cannot read them. Mirrors the API's carrier table.
 */
export const FREIGHT_CARRIERS: ReadonlySet<string> = new Set([
  'fedex_freight', 'xpo', 'averitt', 'saia', 'old_dominion', 'estes', 'rl_carriers', 'abf', 'southeastern', 'pilot', 'pitt_ohio',
]);

/** Carriers Shippo tracks; everything else takes a hand-set status. */
export const SHIPPO_CARRIERS: ReadonlySet<string> = new Set(['fedex', 'fedex_freight', 'ups', 'usps', 'dhl_express', 'dhl_ecommerce']);

/** Whether a person sets this shipment's status rather than the carrier feed. */
export function statusIsManual(carrier: string | undefined): boolean {
  return !carrier || !SHIPPO_CARRIERS.has(carrier);
}

/** "Pallet 2 of 3" / "Box 1 of 1", or undefined when nothing is recorded. */
export function piecesLabel(shipment: Pick<Shipment, 'pieces' | 'pieceType'>): string | undefined {
  const count = shipment.pieces?.count;
  const of = shipment.pieces?.of;
  if (!count && !of) return undefined;
  const word = shipment.pieceType === 'pallet' ? 'Pallet' : 'Box';
  return `${word} ${count ?? '__'} of ${of ?? '__'}`;
}

/** A carrier value the dropdown knows about. */
export type ShipmentCarrier = keyof typeof SHIPMENT_CARRIERS;

/**
 * A carrier's display name. Legacy shipments free-typed the carrier
 * ("XPO Logistics", "Averitt Express"), so anything unrecognised shows as
 * stored rather than being hidden or relabelled.
 *
 * @param carrier - The stored carrier value.
 * @returns Something always renderable.
 */
export function carrierLabel(carrier: string | undefined): string {
  if (!carrier) return '—';
  return SHIPMENT_CARRIERS[carrier as ShipmentCarrier] ?? carrier;
}

/** One Shippo tracking scan — trackingStatus and each trackingHistory entry. */
export interface TrackingEvent {
  readonly status?: string;
  readonly statusDetails?: string;
  readonly statusDate?: string;
  readonly location?: {
    readonly city?: string;
    readonly state?: string;
    readonly zip?: string;
    readonly country?: string;
  };
}

/** A shipment, on an event or in the global list. */
export interface Shipment {
  readonly _id: string;
  readonly carrier?: string;
  readonly trackingNumber?: string;
  /** LTL freight reference; freight carriers link and track by it. */
  readonly proNumber?: string;
  readonly pieces?: { readonly count?: number; readonly of?: number };
  readonly pieceType?: 'box' | 'pallet';
  readonly vendor?: string;
  readonly status?: string;
  readonly eta?: string;
  readonly expectedArrivalDate?: string;
  readonly viewableByClient?: boolean;
  /** A prepaid label for shipping the materials back; never on the client's portal. */
  readonly returnLabel?: boolean;
  /** A return label no carrier scanned a week after the event; the API works it out. */
  readonly returnLabelUnused?: boolean;
  /** What the venue wants written on the box label, line breaks kept. */
  readonly labelText?: string;
  readonly link?: string;
  readonly items?: readonly { readonly name?: string; readonly quantity?: string | number; readonly packingLineId?: string }[];
  readonly addressTo?: ShipmentAddress;
  readonly addressFrom?: ShipmentAddress;
  readonly trackingStatus?: TrackingEvent;
  /** Carrier scans as Shippo reported them, oldest first. */
  readonly trackingHistory?: readonly TrackingEvent[];
  readonly relatedTo?:
    | {
        readonly _id: string;
        readonly programName?: string;
        readonly programDisplayName?: string;
        readonly contractNumber?: string;
        readonly eventDate?: string;
        readonly account?: { readonly name?: string };
      }
    | string
    | null;
}

/**
 * Shipments for one event.
 *
 * @param eventId - Mongo id.
 * @param signal - Abort signal.
 * @returns The shipments.
 */
export async function listEventShipments(
  eventId: string,
  signal?: AbortSignal,
): Promise<readonly Shipment[]> {
  const response = await axios.get<{ data: Shipment[] }>(
    `/api/v3/events/${eventId}/shipments`,
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

/** Which return labels the shipments list narrows to; mirrors the API's RETURN_LABEL_VIEW. */
export const RETURN_LABEL_VIEW = {
  all: 'all',
  unused: 'unused',
} as const;
export type ReturnLabelView = (typeof RETURN_LABEL_VIEW)[keyof typeof RETURN_LABEL_VIEW];

/** One page of the global shipments list. */
export interface ShipmentsPage {
  readonly shipments: readonly Shipment[];
  readonly page: number;
  readonly totalPages: number;
  readonly total: number;
}

/**
 * Every shipment, for the operations view — a paginated list narrowed by an
 * optional carrier/tracking search.
 *
 * @param params - Page number and optional search.
 * @param signal - Abort signal.
 * @returns One page of shipments plus pagination.
 */
export async function listAllShipments(
  params: {
    page: number;
    search?: string;
    status?: ShipmentStatus;
    returnLabels?: ReturnLabelView;
    sort?: { readonly key: string; readonly direction: 'asc' | 'desc' };
  },
  signal?: AbortSignal,
): Promise<ShipmentsPage> {
  const response = await axios.get<{
    data: Shipment[];
    pagination?: { page?: number; totalPages?: number; total?: number };
  }>('/api/v3/shipments', {
    params: {
      page: params.page,
      limit: 25,
      ...(params.search?.trim() ? { search: params.search.trim() } : {}),
      // Bracket form again: the controller reads `where.status`.
      ...(params.status ? { [`where[status]`]: params.status } : {}),
      ...(params.returnLabels ? { returnLabels: params.returnLabels } : {}),
      // Express parses the bracket form back into the orderBy object the
      // controller reads.
      ...(params.sort ? { [`orderBy[${params.sort.key}]`]: params.sort.direction } : {}),
    },
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return {
    shipments: response.data.data,
    page: response.data.pagination?.page ?? params.page,
    totalPages: response.data.pagination?.totalPages ?? 1,
    total: response.data.pagination?.total ?? response.data.data.length,
  };
}

/**
 * One shipment, in full — the single-shipment page's data.
 *
 * @param shipmentId - Mongo id.
 * @param signal - Abort signal.
 * @returns The shipment.
 */
/** What a tracking refresh reports back. */
export interface ShipmentSyncResult {
  readonly synced: boolean;
  /** Why nothing changed — untrackable carrier, or no carrier update yet. */
  readonly reason?: string;
}

/**
 * Refreshes one shipment's tracking from the carrier.
 *
 * @param shipmentId - Mongo id.
 * @returns Whether the carrier had anything new.
 */
export async function syncShipmentTracking(shipmentId: string): Promise<ShipmentSyncResult> {
  const response = await axios.post<{ data: ShipmentSyncResult }>(
    `/api/v3/shipments/${shipmentId}/sync`,
    {},
    { headers: await authHeader() },
  );
  return response.data.data;
}

export async function readShipment(shipmentId: string, signal?: AbortSignal): Promise<Shipment> {
  const response = await axios.get<{ data: Shipment }>(`/api/v3/shipments/${shipmentId}`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/** The staff-editable shipment fields, mirroring the V3 write schema. */
export interface ShipmentPatch {
  readonly carrier: string;
  readonly trackingNumber: string;
  readonly proNumber?: string;
  readonly pieces?: { readonly count?: number; readonly of?: number } | null;
  readonly pieceType?: 'box' | 'pallet';
  /** Only for carriers without a feed; the API refuses it otherwise. */
  readonly status?: string;
  readonly vendor: string;
  readonly expectedArrivalDate?: string;
  readonly viewableByClient: boolean;
  readonly returnLabel?: boolean;
  /** An empty string clears it. */
  readonly labelText?: string;
  readonly items: readonly { readonly name: string; readonly quantity: number; readonly packingLineId?: string }[];
}

/** The "materials shipped" email as it would go, for the dialog to show before Send. */
export async function previewMaterialsShippedEmail(
  eventId: string,
  note: string,
  signal?: AbortSignal,
): Promise<{ readonly subject: string; readonly html: string; readonly text: string }> {
  const response = await axios.get<{ data: { subject: string; html: string; text: string } }>(
    `/api/v3/events/${eventId}/shipments/email/preview`,
    { params: note ? { note } : {}, headers: await authHeader(), signal },
  );
  return response.data.data;
}

/** The "materials shipped" email the program manager sends the client. */
export async function sendMaterialsShippedEmail(
  eventId: string,
  body: { readonly to: readonly string[]; readonly note?: string; readonly subject?: string; readonly html?: string },
): Promise<{ readonly sentAt: string; readonly to: readonly string[]; readonly subject: string }> {
  const response = await axios.post<{ data: { sentAt: string; to: string[]; subject: string } }>(
    `/api/v3/events/${eventId}/shipments/email`,
    body,
    { headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Books a new shipment on an event.
 *
 * @param eventId - The event the shipment belongs to.
 * @param body - The shipment fields.
 * @returns The created shipment.
 */
export async function createEventShipment(eventId: string, body: ShipmentPatch): Promise<Shipment> {
  const response = await axios.post<{ data: Shipment }>(
    `/api/v3/events/${eventId}/shipments`,
    body,
    { headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Updates a shipment.
 *
 * @param shipmentId - Mongo id.
 * @param patch - The fields to change.
 * @returns The updated shipment.
 */
export async function updateShipment(shipmentId: string, patch: ShipmentPatch): Promise<Shipment> {
  const response = await axios.put<{ data: Shipment }>(
    `/api/v3/shipments/${shipmentId}`,
    patch,
    { headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Deletes a shipment.
 *
 * @param shipmentId - Mongo id.
 */
export async function deleteShipment(shipmentId: string): Promise<void> {
  await axios.delete(`/api/v3/shipments/${shipmentId}`, { headers: await authHeader() });
}

// ─── Logistics (facilitator dashboard) ──────────────────────────────────────

/** The five logistics sections, in display order. */
export const LOGISTICS_SECTIONS = [
  'supplies',
  'assistants',
  'flight',
  'hotel',
  'rentalCar',
] as const;
export type LogisticsSection = (typeof LOGISTICS_SECTIONS)[number];

/** A postal address on a logistics section. */
export interface LogisticsAddress {
  readonly name?: string;
  readonly street?: string;
  readonly city?: string;
  readonly state?: string;
  readonly zip?: string;
}

/** One logistics section's answers — the union of every section's fields. */
export interface LogisticsSectionData {
  readonly isNeeded?: 'yes' | 'no' | '';
  readonly status?: string;
  readonly comments?: string;
  readonly items?: readonly { readonly name: string; readonly quantity?: number }[];
  readonly count?: number;
  readonly airline?: string;
  readonly company?: string;
  readonly confirmationNumber?: string;
  readonly address?: LogisticsAddress;
  readonly checkIn?: string;
  readonly checkOut?: string;
}

/** The whole submission, keyed by section. */
export type LogisticsSubmission = Partial<Record<LogisticsSection, LogisticsSectionData>>;

/** One facilitator's logistics form on an event. */
export interface LogisticsForm {
  readonly _id: string;
  readonly facilitator: EventPerson | string;
  readonly submission: LogisticsSubmission;
  readonly updatedAt?: string;
}

/**
 * Every facilitator's logistics form on an event (the caller's own included).
 *
 * @param eventId - Mongo id.
 * @param signal - Abort signal.
 * @returns The forms.
 */
export async function listEventLogistics(
  eventId: string,
  signal?: AbortSignal,
): Promise<readonly LogisticsForm[]> {
  const response = await axios.get<{ data: LogisticsForm[] }>(
    `/api/v3/events/${eventId}/logistics`,
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Saves a traveler's logistics form on an event. Omit `travelerId` for your
 * own form; internal staff pass it to manage (or add) any traveler —
 * contractors can never write anyone else's.
 *
 * @param eventId - Mongo id.
 * @param submission - The five-section submission.
 * @param travelerId - Whose form to write (internal only).
 * @returns The saved form.
 */
export async function saveEventLogistics(
  eventId: string,
  submission: LogisticsSubmission,
  travelerId?: string,
): Promise<LogisticsForm> {
  const response = await axios.put<{ data: LogisticsForm }>(
    `/api/v3/events/${eventId}/logistics`,
    { submission, ...(travelerId ? { travelerId } : {}) },
    { headers: await authHeader() },
  );
  return response.data.data;
}

// ─── Attachments ────────────────────────────────────────────────────────────

/** Who may see an attachment. Mirrors the API's `ATTACHMENT_AUDIENCE`. */
export const ATTACHMENT_AUDIENCE = {
  internal: 'internal',
  contractor: 'contractor',
  client: 'client',
} as const;
export type AttachmentAudience = (typeof ATTACHMENT_AUDIENCE)[keyof typeof ATTACHMENT_AUDIENCE];

/** A file attached to an event. */
export interface EventAttachment {
  readonly _id: string;
  readonly name: string;
  readonly type?: string;
  readonly description?: string;
  readonly preview?: string;
  readonly viewableBy?: readonly AttachmentAudience[];
  readonly size?: number;
  readonly uploadedBy?: { readonly _id?: string; readonly firstName?: string; readonly lastName?: string } | string;
  /** Set on uploads from the client portal, which have no staff user to point at. */
  readonly uploadedByName?: string | null;
  readonly uploadedBySide?: 'CLIENT' | 'BCE' | null;
  /** Tags that route a document to a card in the portal, e.g. `csr`. */
  readonly tags?: readonly string[];
  /** A typed role, e.g. `CSR_TAX_LETTER`. */
  readonly documentType?: string | null;
  readonly createdAt?: string;
}

/**
 * Upload limits, checked before a request leaves the browser so the user
 * hears about a bad file at once. The API enforces the same values
 * (`MAX_ATTACHMENT_BYTES`, `ATTACHMENT_ALLOWED_TYPES` in
 * event-attachments.controller.ts); keep the two in step.
 */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const ATTACHMENT_ACCEPT: readonly string[] = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/heic',
  'video/mp4',
  'video/quicktime',
  'text/plain',
  'text/csv',
  'application/rtf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

/**
 * Why a file cannot be uploaded, or undefined when it can. Pure, so the tab
 * can show the reason without a request.
 *
 * @param file - The chosen file.
 * @returns A short message for the user, or undefined.
 */
export function attachmentRejection(file: { size: number; type: string }): string | undefined {
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return `Over the ${ATTACHMENT_MAX_BYTES / (1024 * 1024)} MB limit`;
  }
  if (!ATTACHMENT_ACCEPT.includes(file.type)) {
    return file.type ? `${file.type} files cannot be attached` : 'Unknown file type';
  }
  return undefined;
}

/**
 * Attachments on an event.
 *
 * @param eventId - Mongo id.
 * @param signal - Abort signal.
 * @returns The attachments.
 */
export async function listEventAttachments(
  eventId: string,
  signal?: AbortSignal,
): Promise<readonly EventAttachment[]> {
  const response = await axios.get<{ data: EventAttachment[] }>(
    `/api/v3/events/${eventId}/attachments`,
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Uploads one file to an event's attachments. The server takes the name and
 * type from the file itself.
 *
 * @param eventId - The event.
 * @param file - The file to attach.
 * @param options - Who may see it; defaults server-side to staff only.
 */
export async function uploadEventAttachment(
  eventId: string,
  file: File,
  options: {
    readonly name?: string;
    readonly description?: string;
    readonly viewableBy?: readonly AttachmentAudience[];
    /** Its role on the portal's CSR card; tags it for the card as well. */
    readonly documentType?: 'CSR_PHOTO' | 'CSR_TAX_LETTER' | 'CSR_ACKNOWLEDGEMENT';
  } = {},
): Promise<void> {
  const form = new FormData();
  form.append('file', file);
  if (options.name?.trim()) form.append('name', options.name.trim());
  if (options.description?.trim()) form.append('description', options.description.trim());
  for (const audience of options.viewableBy ?? []) form.append('viewableBy', audience);
  if (options.documentType) form.append('documentType', options.documentType);
  await axios.post(`/api/v3/events/${eventId}/attachments`, form, {
    headers: await authHeader(),
  });
}

/**
 * Removes an attachment and its stored file.
 *
 * @param attachmentId - The attachment.
 */
export async function deleteEventAttachment(attachmentId: string): Promise<void> {
  await axios.delete(`/api/v3/attachments/${attachmentId}`, { headers: await authHeader() });
}

/** Editable attachment facts: the title, the description, and who may see it. */
export interface AttachmentPatch {
  readonly name?: string;
  readonly description?: string;
  readonly viewableBy?: readonly AttachmentAudience[];
}

/**
 * Updates an attachment's title, description or audience.
 *
 * @param attachmentId - The attachment.
 * @param patch - The fields to change.
 */
export async function updateEventAttachment(attachmentId: string, patch: AttachmentPatch): Promise<void> {
  await axios.put(`/api/v3/attachments/${attachmentId}`, patch, { headers: await authHeader() });
}

/** Where a checklist item came from: the program's template, or the team's own hand. */
export const CHECKLIST_ITEM_KIND = { template: 'TEMPLATE', custom: 'CUSTOM' } as const;
export type ChecklistItemKind = (typeof CHECKLIST_ITEM_KIND)[keyof typeof CHECKLIST_ITEM_KIND];

/** One checklist item, merged template + this event's status. */
export interface ChecklistItem {
  readonly _id: string;
  readonly kind: ChecklistItemKind;
  readonly name: string;
  readonly tags: readonly string[];
  /** The heading a hand-added item was filed under; null on template items, which group by tag. */
  readonly section: string | null;
  readonly checked: boolean;
  readonly applicable: boolean;
}

/** The event's BCE checklist, and the template items taken off it. */
export interface ChecklistView {
  readonly items: readonly ChecklistItem[];
  readonly removed: readonly { readonly _id: string; readonly name: string }[];
}

/** One item as the checklist is written back: the whole visible list, plus any removal or restore. */
export type ChecklistItemWrite =
  | {
      readonly kind: typeof CHECKLIST_ITEM_KIND.template;
      readonly _id: string;
      readonly checked: boolean;
      readonly applicable: boolean;
      readonly removed?: boolean;
    }
  | {
      readonly kind: typeof CHECKLIST_ITEM_KIND.custom;
      readonly _id?: string;
      readonly name: string;
      readonly section?: string;
      readonly checked: boolean;
      readonly applicable: boolean;
    };

/**
 * The event's BCE checklist — the template's items less any removed, with
 * their done / not-applicable status, plus what the team added by hand.
 *
 * @param eventId - Mongo id.
 * @param signal - Abort signal.
 * @returns The list and the removed template items.
 */
export async function listChecklist(eventId: string, signal?: AbortSignal): Promise<ChecklistView> {
  const response = await axios.get<{ data: ChecklistView }>(
    `/api/v3/events/${eventId}/checklist`,
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Persists the whole checklist for an event.
 *
 * @param eventId - Mongo id.
 * @param items - Every visible item, and any template item being removed or restored.
 */
export async function saveChecklist(eventId: string, items: readonly ChecklistItemWrite[]): Promise<void> {
  await axios.put(
    `/api/v3/events/${eventId}/checklist`,
    { checklistItems: items },
    { headers: await authHeader() },
  );
}

// ─── Recaps ─────────────────────────────────────────────────────────────────

/** One answer inside a recap. */
export interface RecapAnswer {
  readonly name: string;
  readonly question: string;
  readonly answer?: unknown;
  readonly type?: 'yes_no_na' | 'comment';
  readonly conditionals?: Record<string, unknown>;
}

/** A facilitator's post-event recap. */
export interface EventRecap {
  readonly _id: string;
  readonly event?:
    | {
        readonly _id: string;
        readonly programName?: string;
        readonly contractNumber?: string;
        readonly account?: { readonly name?: string };
        readonly eventDate?: string;
        /** The event's real start instant and zone — see `@/lib/event-time`. */
        readonly timeStart?: string | null;
        readonly timeZone?: string | null;
        readonly longTimeZone?: string | null;
      }
    | string
    | null;
  readonly facilitator?:
    | { readonly _id?: string; readonly firstName?: string; readonly lastName?: string; readonly email?: string; readonly avatar?: string }
    | string
    | null;
  readonly recap: readonly RecapAnswer[];
  /** Feedback for the client, shown on their portal; absent or null when there is none. */
  readonly clientFeedback?: string | null;
  readonly clientFeedbackAt?: string | null;
  readonly createdAt?: string;
  readonly updatedAt?: string;
}

/** The longest feedback for the client the API accepts. Mirrors `CLIENT_FEEDBACK_MAX_LENGTH`. */
export const CLIENT_FEEDBACK_MAX_LENGTH = 4000;

/** Roles that may edit any facilitator's recap, and so correct its feedback. Mirrors the API. */
const RECAP_STAFF_ROLES: ReadonlySet<string> = new Set(['internal', 'admin', 'superadmin']);

/**
 * Whether the caller may correct another facilitator's feedback for the client.
 *
 * @param role - The caller's role.
 * @returns True for BCE staff.
 */
export function editsAnyRecap(role: string | undefined): boolean {
  return role !== undefined && RECAP_STAFF_ROLES.has(role);
}

/**
 * Fetches one recap, with its event and facilitator populated.
 *
 * @param recapId - Mongo id.
 * @param signal - Abort signal.
 * @returns The recap.
 */
export async function readRecap(recapId: string, signal?: AbortSignal): Promise<EventRecap> {
  const response = await axios.get<{ data: EventRecap }>(`/api/v3/event-recaps/${recapId}`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/**
 * Submits a new recap for the current facilitator on an event.
 *
 * @param input - Event id, facilitator (author) id, and the answered items.
 * @returns The created recap.
 */
export async function createRecap(input: {
  event: string;
  facilitator: string;
  recap: readonly RecapAnswer[];
  /** Sent only when the event is attached to a portal booking. */
  clientFeedback?: string | null;
}): Promise<EventRecap> {
  const response = await axios.post<{ data: EventRecap }>('/api/v3/event-recaps', input, {
    headers: await authHeader(),
  });
  return response.data.data;
}

/**
 * Updates an existing recap: its answers, its feedback for the client, or
 * both. Staff correcting feedback send only that.
 *
 * @param recapId - Mongo id of the recap.
 * @param changes - The answered items and/or the feedback.
 * @returns The updated recap.
 */
export async function updateRecap(
  recapId: string,
  changes: { readonly recap?: readonly RecapAnswer[]; readonly clientFeedback?: string | null },
): Promise<EventRecap> {
  const response = await axios.put<{ data: EventRecap }>(
    `/api/v3/event-recaps/${recapId}`,
    changes,
    { headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Recaps, optionally for one event.
 *
 * @param options - Optional event filter and search.
 * @param signal - Abort signal.
 * @returns Recaps plus total.
 */
export async function listRecaps(
  options: {
    readonly eventId?: string;
    readonly search?: string;
    readonly sort?: { readonly key: string; readonly direction: 'asc' | 'desc' };
    /** Awaiting-contract events are hidden unless 'include' (or 'only'). */
    readonly awaitingContract?: 'include' | 'only';
  },
  signal?: AbortSignal,
): Promise<{ readonly recaps: readonly EventRecap[]; readonly total: number }> {
  const params: Record<string, string | number> = { limit: 50 };
  if (options.eventId) params['event'] = options.eventId;
  if (options.search?.trim()) params['search'] = options.search.trim();
  if (options.sort) params[`orderBy[${options.sort.key}]`] = options.sort.direction;
  const response = await axios.get<{ data: EventRecap[]; pagination?: { total?: number } }>(
    '/api/v3/event-recaps',
    { params, ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return {
    recaps: response.data.data,
    total: response.data.pagination?.total ?? response.data.data.length,
  };
}

// ─── Integrations ───────────────────────────────────────────────────────────

/** The third-party integrations the platform connects to. */
export const INTEGRATION_PROVIDERS = ['google', 'salesforce', 'quickbooks'] as const;
export type IntegrationProvider = (typeof INTEGRATION_PROVIDERS)[number];

/** Connection state of one integration. */
export interface IntegrationStatus {
  readonly integration: string;
  readonly status: 'connected' | 'disconnected';
}

/**
 * Whether an integration currently holds credentials.
 *
 * @param provider - The integration.
 * @param signal - Abort signal.
 * @returns The status.
 */
export async function readIntegrationStatus(
  provider: IntegrationProvider,
  signal?: AbortSignal,
): Promise<IntegrationStatus> {
  const response = await axios.get<{ data: IntegrationStatus }>(
    `/api/v3/integrations/${provider}/status`,
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Asks the API for the provider's OAuth authorization URL. Navigating to it
 * runs the provider's consent flow and lands back on /integrations.
 *
 * @param provider - The integration.
 * @returns The URL to navigate to.
 */
export async function createIntegrationConnectUrl(
  provider: IntegrationProvider,
): Promise<string> {
  const response = await axios.post<{ data: { url: string } }>(
    `/api/v3/integrations/${provider}/connect-url`,
    { returnTo: window.location.origin },
    { headers: await authHeader() },
  );
  return response.data.data.url;
}

/**
 * Disconnects an integration (clears its stored credentials).
 *
 * @param provider - The integration.
 */
export async function disconnectIntegration(provider: IntegrationProvider): Promise<void> {
  await axios.post(
    `/api/v3/integrations/${provider}/disconnect`,
    {},
    { headers: await authHeader() },
  );
}

/**
 * Resolves stored email values to user cards (case-insensitive). Emails with
 * no user record are absent from the map — render those as plain addresses.
 *
 * @param emails - The addresses to resolve.
 * @param signal - Abort signal.
 * @returns A lowercase-email → person map.
 */
export async function lookupUsersByEmails(
  emails: readonly string[],
  signal?: AbortSignal,
): Promise<ReadonlyMap<string, DirectoryPerson>> {
  if (emails.length === 0) return new Map();
  const response = await axios.post<{ data: DirectoryPerson[] }>(
    '/api/v3/users/lookup-by-emails',
    { emails },
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return new Map(
    response.data.data.flatMap((person) =>
      person.email ? [[person.email.toLowerCase(), person] as const] : [],
    ),
  );
}

// ─── System settings ────────────────────────────────────────────────────────

/** Free-text settings: the default managers assigned to new events. */
export const MANAGER_SETTINGS = [
  'default-accounting-manager',
  'default-contract-manager',
  'default-production-manager',
  'default-program-manager',
] as const;
export type ManagerSetting = (typeof MANAGER_SETTINGS)[number];

/** Email-list settings: notification recipients and team contact groups. */
export const EMAIL_LIST_SETTINGS = [
  'event-recap',
  'facilitator-assignment-notify',
  'charity-contacts',
  'production-contacts',
  'smarthunts-contacts',
] as const;
export type EmailListSetting = (typeof EMAIL_LIST_SETTINGS)[number];

/** The switch map behind the standard client portal: what it shows by default. */
export const STANDARD_PORTAL_FEATURES_SETTING = 'standard-portal-features';
export type FeatureFlagSetting = typeof STANDARD_PORTAL_FEATURES_SETTING;

/** The list price of one credit (§46), before a client's preferred-pricing discount. */
export const CREDIT_LIST_PRICE_SETTING = 'credit-list-price';
/** The company minimum profit margin, percent of price, the pricing explorer checks discounts against. Mirrors the API. */
export const PRICING_MINIMUM_MARGIN_SETTING = 'pricing-minimum-margin';
export const DEFAULT_MINIMUM_MARGIN_PERCENT = 45;
export const MAX_MINIMUM_MARGIN_PERCENT = 95;
export type NumberSetting = typeof CREDIT_LIST_PRICE_SETTING | typeof PRICING_MINIMUM_MARGIN_SETTING;

/** The ordered program-manager assignment ruleset (BES-34). */
export const PROGRAM_MANAGER_RULES_SETTING = 'program-manager-rules';
export type RuleSetting = typeof PROGRAM_MANAGER_RULES_SETTING;

/** Create website leads in Salesforce ourselves, off the Pardot form handlers. Mirrors the API. */
export const WEBSITE_LEADS_DIRECT_SETTING = 'website-leads-direct-to-salesforce';
/** Give an existing client's website Lead to their account owner. Mirrors the API. */
export const LEAD_OWNER_FROM_ACCOUNT_SETTING = 'website-lead-owner-from-account';
export type LeadSwitchSetting = typeof WEBSITE_LEADS_DIRECT_SETTING | typeof LEAD_OWNER_FROM_ACCOUNT_SETTING;

/** Offer website leads a call on their Lead owner's calendar. Mirrors the API. */
export const LEAD_MEETING_OFFER_SETTING = 'lead-meeting-offer';
export type MeetingOfferSettingName = typeof LEAD_MEETING_OFFER_SETTING;

/** The `lead-meeting-offer` setting value. */
export interface LeadMeetingOffer {
  readonly enabled: boolean;
  readonly durationMinutes: MeetingDuration;
  readonly surfaces: { readonly acknowledgementEmail: boolean; readonly thankYouPage: boolean };
}

/** What an unset setting means: no offer anywhere. Mirrors the API's default. */
export const DEFAULT_LEAD_MEETING_OFFER: LeadMeetingOffer = {
  enabled: false,
  durationMinutes: 15,
  surfaces: { acknowledgementEmail: false, thankYouPage: false },
};

/**
 * The program-manager categories authored on the website (BES-34). Mirrors
 * the API's `PROGRAM_MANAGER_CATEGORIES`; the two must stay in step.
 */
export const PROGRAM_MANAGER_CATEGORIES = [
  'Game Show',
  'SmartHunts',
  'Facilitator-led',
  'Virtual',
] as const;
export type ProgramManagerCategory = (typeof PROGRAM_MANAGER_CATEGORIES)[number];

/** How broadly a rule applies. Array order is precedence; first match wins. */
export const RULE_SCOPES = ['all', 'category', 'program'] as const;
export type RuleScope = (typeof RULE_SCOPES)[number];

/**
 * One program-manager assignment rule. Category/programId are only meaningful
 * for their scope; the API validates and strips the rest on save.
 */
export interface ProgramManagerRule {
  readonly id: string;
  /** Optional human label shown on the rule's collapsed accordion header. */
  readonly name?: string;
  readonly scope: RuleScope;
  readonly category?: ProgramManagerCategory;
  readonly programId?: string;
  readonly programName?: string;
  readonly minParticipants?: number | null;
  readonly maxParticipants?: number | null;
  /** The assignee's Salesforce Contact id, or null for "See Facilitator". */
  readonly assignContactId: string | null;
  readonly assignContactName?: string;
  readonly active: boolean;
}

/** A candidate program-manager assignee, as the API lists them. */
export interface ProgramManagerContact {
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
}

/** The operational settings map as the API returns it. */
export type SystemSettings = Partial<
  Record<ManagerSetting, string | null> &
    Record<EmailListSetting, readonly string[] | null> &
    Record<FeatureFlagSetting, PortalFeatureFlags | null> &
    Record<NumberSetting, number | null> &
    Record<RuleSetting, readonly ProgramManagerRule[] | null> &
    Record<LeadSwitchSetting, boolean | null> &
    Record<MeetingOfferSettingName, LeadMeetingOffer | null>
>;

/** One setting write. */
export type SystemSettingWrite =
  | { readonly name: ManagerSetting; readonly value: string }
  | { readonly name: EmailListSetting; readonly value: readonly string[] }
  | { readonly name: FeatureFlagSetting; readonly value: PortalFeatureFlags }
  | { readonly name: NumberSetting; readonly value: number }
  | { readonly name: RuleSetting; readonly value: readonly ProgramManagerRule[] }
  | { readonly name: typeof APP_FEATURES_SETTING; readonly value: AppFeatureFlags }
  | { readonly name: LeadSwitchSetting; readonly value: boolean }
  | { readonly name: MeetingOfferSettingName; readonly value: LeadMeetingOffer };

/**
 * The operational system settings (default managers, notification lists,
 * team contacts). Admin-only.
 *
 * @param signal - Abort signal.
 * @returns The settings map.
 */
export async function readSystemSettings(signal?: AbortSignal): Promise<SystemSettings> {
  const response = await axios.get<{ data: SystemSettings }>('/api/v3/system-settings', {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/**
 * Writes the given operational settings.
 *
 * @param settings - The settings to write.
 */
export async function saveSystemSettings(
  settings: readonly SystemSettingWrite[],
): Promise<void> {
  await axios.put('/api/v3/system-settings', { settings }, { headers: await authHeader() });
}

/**
 * The candidate program-manager assignees for the rules UI: internal
 * Salesforce Contacts on the company domain, plus the known PMs. Admin-only.
 *
 * @param signal - Abort signal.
 * @returns The candidate contacts, sorted by name.
 */
export async function listProgramManagers(
  signal?: AbortSignal,
): Promise<readonly ProgramManagerContact[]> {
  const response = await axios.get<{ data: ProgramManagerContact[] }>(
    '/api/v3/system-settings/program-managers',
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

// ─── Programs ───────────────────────────────────────────────────────────────

/** A program in BCE's catalog, as V3 lists it. */
export interface Program {
  readonly _id: string;
  readonly name?: string;
  readonly displayName?: string;
  readonly excerpt?: string;
  readonly description?: string;
  /** The website's full rendered HTML for the program — headings, lists and
   * all. The plain `description` is this with every tag stripped. */
  readonly htmlContent?: string;
  readonly proposalDescription?: string;
  readonly unbrandedDescription?: string;
  /** Unbranded HTML twin — populated by the sync once legacy ships the
   * matching change; empty until a program re-syncs after that. */
  readonly unbrandedHtmlContent?: string;
  readonly category?: string;
  readonly tags?: readonly string[];
  /** Season and occasion tags by id (Catalog → Season tags). */
  readonly seasonTagIds?: readonly string[];
  readonly groupSize?: string;
  readonly teamSize?: string;
  /** The default kit: catalog items with a quantity rule each. */
  readonly kit?: readonly KitLine[];
  readonly length?: string;
  readonly setting?: string;
  readonly physicality?: string;
  readonly spaceRequirement?: string;
  readonly pricingIncludes?: string;
  readonly pricingType?: string;
  readonly images?: readonly string[];
  readonly video?: string;
  readonly unbrandedVideo?: string;
  readonly isActive?: boolean;
  readonly onWebsite?: boolean;
  readonly websiteId?: string;
  readonly salesforceId?: string;
  readonly salesforceProgramId?: string;
  readonly createdAt?: string;
  readonly updatedAt?: string;
}

/** The subset of program fields the edit form can change. */
export type ProgramPatch = Partial<
  Pick<
    Program,
    | 'name'
    | 'excerpt'
    | 'description'
    | 'proposalDescription'
    | 'unbrandedDescription'
    | 'groupSize'
    | 'teamSize'
    | 'length'
    | 'setting'
    | 'physicality'
    | 'spaceRequirement'
    | 'pricingIncludes'
    | 'pricingType'
    | 'video'
    | 'unbrandedVideo'
    | 'isActive'
  >
> & { readonly tags?: readonly string[]; readonly seasonTagIds?: readonly string[] };

/**
 * Re-syncs one program from Salesforce by its Salesforce id.
 *
 * @param salesforceId - The program's Salesforce id.
 */
export async function syncProgram(salesforceId: string): Promise<void> {
  await axios.post('/api/v3/programs/sync', { salesforceId }, { headers: await authHeader() });
}

/**
 * Updates a program record.
 *
 * @param id - The program's Mongo id.
 * @param patch - The fields to change.
 * @returns The updated program.
 */
export async function updateProgram(id: string, patch: ProgramPatch): Promise<Program> {
  const response = await axios.put<{ data: Program }>(`/api/v3/programs/${id}`, patch, {
    headers: await authHeader(),
  });
  return response.data.data;
}

/**
 * Deletes a program record.
 *
 * @param id - The program's Mongo id.
 */
export async function deleteProgram(id: string): Promise<void> {
  await axios.delete(`/api/v3/programs/${id}`, { headers: await authHeader() });
}

/** The most programs one list request returns (the API's page-size cap). */
export const PROGRAM_LIST_MAX = 200;

/**
 * The program catalog.
 *
 * @param search - Optional name search.
 * @param signal - Abort signal.
 * @param options - The catalog slice (default: every program) and page size.
 * @returns Programs plus total.
 */
export async function listPrograms(
  search: string,
  signal?: AbortSignal,
  options: { readonly catalog?: ProgramCatalog; readonly limit?: number } = {},
): Promise<{ readonly programs: readonly Program[]; readonly total: number }> {
  const params: Record<string, string | number> = { limit: options.limit ?? 100 };
  if (options.catalog) params['catalog'] = options.catalog;
  if (search.trim()) params['search'] = search.trim();
  const response = await axios.get<{ data: Program[]; pagination?: { total?: number } }>(
    '/api/v3/programs',
    { params, ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return {
    programs: response.data.data,
    total: response.data.pagination?.total ?? response.data.data.length,
  };
}

/** The catalog slices the Programs page tabs between (mirrors the API's `?catalog=`). */
export const PROGRAM_CATALOG = {
  website: 'website',
  active: 'active',
  all: 'all',
} as const;

export type ProgramCatalog = (typeof PROGRAM_CATALOG)[keyof typeof PROGRAM_CATALOG];

/** One page of the program catalog. */
export interface ProgramCatalogPage {
  readonly programs: readonly Program[];
  readonly page: number;
  readonly totalPages: number;
  readonly total: number;
}

/**
 * One page of the program catalog, for the Programs page.
 *
 * @param query - Search text, catalog slice, page and page size.
 * @param signal - Abort signal.
 * @returns The page's programs and the pagination totals.
 */
export async function listProgramCatalog(
  query: {
    readonly search: string;
    readonly catalog: ProgramCatalog;
    readonly page: number;
    readonly limit: number;
  },
  signal?: AbortSignal,
): Promise<ProgramCatalogPage> {
  const params: Record<string, string | number> = {
    catalog: query.catalog,
    page: query.page,
    limit: query.limit,
  };
  if (query.search.trim()) params['search'] = query.search.trim();
  const response = await axios.get<{
    data: Program[];
    pagination: { page: number; totalPages: number; total: number };
  }>('/api/v3/programs', { params, ...(signal ? { signal } : {}), headers: await authHeader() });
  return {
    programs: response.data.data,
    page: response.data.pagination.page,
    totalPages: response.data.pagination.totalPages,
    total: response.data.pagination.total,
  };
}

/**
 * One program.
 *
 * @param programId - Mongo id.
 * @param signal - Abort signal.
 * @returns The program.
 */
export async function readProgram(programId: string, signal?: AbortSignal): Promise<Program> {
  const response = await axios.get<{ data: Program }>(`/api/v3/programs/${programId}`, {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/** A row in the profile's event history tables. */
export interface ProfileEventRow {
  readonly id: string;
  readonly contractNumber: string | null;
  /** What this person is on the event: Lead facilitator, Team member, …. */
  readonly role?: string;
  readonly eventDate: string | null;
  /** The event's real start instant and zone — see `@/lib/event-time`. */
  readonly timeStart?: string | null;
  readonly timeEnd?: string | null;
  readonly timeZone?: string | null;
  readonly longTimeZone?: string | null;
  readonly time: string | null;
  readonly client: string | null;
  readonly participants: number | null;
  readonly program: string | null;
  readonly location: string | null;
}

/** A row in the profile's sign-in log. */
export interface UserLoginRow {
  readonly id: string;
  readonly type: string;
  readonly url: string;
  readonly ip: string;
  readonly at: string | null;
}

/** One page of results with its total, from a paginated profile endpoint. */
interface Page<T> {
  readonly rows: readonly T[];
  readonly total: number;
  readonly page: number;
  readonly totalPages: number;
}

/**
 * Fetches a page of a user's events, as facilitator or team member.
 *
 * @param userId - The user's id.
 * @param query - The scope ('upcoming' or 'past'), the 1-based page, and an
 *   optional search (contract, client, program or place).
 * @param signal - Abort signal.
 * @returns One page of event rows plus totals.
 */
export async function readUserEvents(
  userId: string,
  query: { readonly scope: 'upcoming' | 'past'; readonly page: number; readonly search?: string },
  signal?: AbortSignal,
): Promise<Page<ProfileEventRow>> {
  const response = await axios.get<{
    data: ProfileEventRow[];
    pagination: { total: number; page: number; totalPages: number };
  }>(`/api/v3/users/${userId}/events`, {
    params: { scope: query.scope, page: query.page, limit: 10, ...(query.search ? { search: query.search } : {}) },
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return {
    rows: response.data.data,
    total: response.data.pagination.total,
    page: response.data.pagination.page,
    totalPages: response.data.pagination.totalPages,
  };
}

/**
 * Fetches a page of a user's sign-in history.
 *
 * @param userId - The user's id.
 * @param query - The 1-based page and an optional search (URL or IP).
 * @param signal - Abort signal.
 * @returns One page of login rows plus totals.
 */
export async function readUserLogins(
  userId: string,
  query: { readonly page: number; readonly search?: string },
  signal?: AbortSignal,
): Promise<Page<UserLoginRow>> {
  const response = await axios.get<{
    data: UserLoginRow[];
    pagination: { total: number; page: number; totalPages: number };
  }>(`/api/v3/users/${userId}/logins`, {
    params: { page: query.page, limit: 10, ...(query.search ? { search: query.search } : {}) },
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return {
    rows: response.data.data,
    total: response.data.pagination.total,
    page: response.data.pagination.page,
    totalPages: response.data.pagination.totalPages,
  };
}

/** Fields an admin may change on any user. */
export interface UserPatch {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly jobTitle?: string;
  readonly company?: string;
  readonly role?: string;
  readonly address?: {
    readonly street?: string;
    readonly city?: string;
    readonly state?: string;
    readonly postalCode?: string;
  };
  readonly associatedEmails?: readonly string[];
  readonly avatar?: string | null;
  readonly schedulingLinks?: readonly SchedulingLink[];
}

/**
 * Admin update of any user's profile.
 *
 * @param userId - The user to update.
 * @param patch - The fields to change.
 * @returns The updated profile.
 */
export async function updateUser(userId: string, patch: UserPatch): Promise<PersonProfile> {
  const response = await axios.put<{ data: PersonProfile }>(`/api/v3/users/${userId}`, patch, {
    headers: await authHeader(),
  });
  return response.data.data;
}

/**
 * Self-service update of the caller's own profile (safe fields only — the API
 * ignores email/role/associatedEmails on this path).
 *
 * @param patch - The fields to change.
 * @returns The updated profile.
 */
export async function updateMyProfile(patch: UserPatch): Promise<PersonProfile> {
  const response = await axios.put<{ data: PersonProfile }>('/api/v3/users/me', patch, {
    headers: await authHeader(),
  });
  return response.data.data;
}

/** The permission gating the client-portal administration surface. */
export const PERMISSION_MANAGE_CLIENT_PORTALS = 'manage_client_portals';

/** The permission gating the Analytics section (leads, bookings, events, pages). */
export const PERMISSION_VIEW_ANALYTICS = 'view_analytics';

/** The permission gating the assistant capability-gap reports. */
export const PERMISSION_VIEW_ASSISTANT_GAPS = 'view_assistant_gaps';

/** The permission gating the proposal generator (dark-launched, superadmin-assignable). */
export const PERMISSION_GENERATE_PROPOSALS = 'generate_proposals';

/** The permission gating Sales → Contracts (every event contract, across events). */
export const PERMISSION_MANAGE_CONTRACTS = 'manage_contracts';

/** The permission gating Sales → Payments (what every contracted event is owed). */
export const PERMISSION_MANAGE_PAYMENTS = 'manage_payments';

/** The permission gating the development tracker (named people only, 2026-09-27). */
export const PERMISSION_VIEW_DEV_TRACKER = 'view_dev_tracker';

/** The permission gating Analytics → Sales and its stale review queue (management only, hq sales D7). */
export const PERMISSION_VIEW_SALES_DASHBOARD = 'view_sales_dashboard';

/** The permission for Sales → Repeat clients, which ships apart from the sales workspace (Thomas, 2026-10-23). */
export const PERMISSION_WORK_REPEAT_CLIENTS = 'work_repeat_clients';

/** The permission for the Lead Control Panel's assign and disqualify actions (Jessica, 2026-10-09). */
export const PERMISSION_MANAGE_LEAD_ASSIGNMENT = 'manage_lead_assignment';

/** The permission gating Analytics → Accounting: expenses, P&L and the reports (named people only, 2026-10-03). */
export const PERMISSION_VIEW_ACCOUNTING = 'view_accounting';

/** The permission gating Tools → Invoices: vendor invoices read into expense lines (2026-10-07). */
export const PERMISSION_PROCESS_INVOICES = 'process_invoices';

/** The permission for Settings → Roles: create, edit and assign roles (RBAC, 2026-10-19). */
export const PERMISSION_MANAGE_ROLES = 'manage_roles';

/** The signed-in staff member, as GET /users/me returns them. */
export interface MyIdentity {
  readonly id: string;
  readonly role: string;
  readonly email?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly phone?: string;
  readonly company?: string;
  readonly address?: {
    readonly street?: string;
    readonly city?: string;
    readonly state?: string;
    readonly postalCode?: string;
  };
  /** Hand-granted flag: may see the assistant capability-gap digests. */
  readonly developer?: boolean;
  /** Permissions the caller holds through roles and direct grants (all, for superadmins). */
  readonly permissions?: readonly string[];
  /** The roles assigned to the caller (empty while RBAC is switched off). */
  readonly roles?: readonly { readonly id: string; readonly name: string }[];
  /** Whether the RBAC rollout switch is on; the Roles screens exist only then. */
  readonly rbacEnabled?: boolean;
  /** Stored avatar value; resolve with {@link avatarSrc} before rendering. */
  readonly avatar?: string | null;
  /** Release-note slugs whose announcement the caller has dismissed. */
  readonly seenReleases?: readonly string[];
  /** Help videos (their CDN file) the caller has started watching. */
  readonly watchedHelpVideos?: readonly string[];
}

/** Dismisses a release announcement for the caller. */
export async function markReleaseSeen(slug: string): Promise<{ seenReleases: string[] }> {
  const response = await axios.post<{ data: { seenReleases: string[] } }>('/api/v3/users/me/releases-seen', { slug }, { headers: await authHeader() });
  return response.data.data;
}

/** Records that the caller started a help video, so its screens stop nudging them. */
export async function markHelpVideoWatched(video: string): Promise<{ watchedHelpVideos: string[] }> {
  const response = await axios.post<{ data: { watchedHelpVideos: string[] } }>('/api/v3/users/me/help-videos-watched', { video }, { headers: await authHeader() });
  return response.data.data;
}

/** Whether the caller holds a permission. */
export function hasPermission(identity: MyIdentity | undefined, permission: string): boolean {
  return identity?.permissions?.includes(permission) ?? false;
}

/**
 * Who am I — id and role, for permission checks in the UI.
 *
 * @param signal - Abort signal.
 * @returns The caller's identity.
 */
export async function readMyIdentity(signal?: AbortSignal): Promise<MyIdentity> {
  const response = await axios.get<{ data: MyIdentity }>('/api/v3/users/me', {
    ...(signal ? { signal } : {}),
    headers: await authHeader(),
  });
  return response.data.data;
}

/** One permission in the catalog, annotated for the current caller. */
export interface PermissionCatalogEntry {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  /** The heading it sits under in the role editor, and its display label. */
  readonly group: string;
  readonly groupLabel: string;
  /** Opens financials or personal data; the role editor flags it. */
  readonly sensitive: boolean;
  /** Account types that may hold it (`staff`, `contractor`, `client`). */
  readonly assignableTo: readonly string[];
  /** Whether the CALLER holds this permission. */
  readonly held: boolean;
  /** Whether the caller may grant/revoke this permission for others. */
  readonly canGrant: boolean;
}

/**
 * The permission catalog, annotated for the caller — powers the profile
 * card's toggles and gating without re-deriving the delegation rule.
 *
 * @param signal - Abort signal.
 * @returns The catalog entries.
 */
export async function readPermissionCatalog(
  signal?: AbortSignal,
): Promise<readonly PermissionCatalogEntry[]> {
  const response = await axios.get<{ data: { permissions: PermissionCatalogEntry[] } }>(
    '/api/v3/permissions',
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data.permissions;
}

/** A user's held permissions (and whether they're an implicit superadmin). */
export interface UserPermissions {
  readonly permissions: readonly string[];
  readonly implicit: boolean;
}

/**
 * The permissions one user holds.
 *
 * @param userId - The user.
 * @param signal - Abort signal.
 * @returns Their permissions.
 */
export async function readUserPermissions(
  userId: string,
  signal?: AbortSignal,
): Promise<UserPermissions> {
  const response = await axios.get<{ data: UserPermissions }>(
    `/api/v3/users/${userId}/permissions`,
    { ...(signal ? { signal } : {}), headers: await authHeader() },
  );
  return response.data.data;
}

/**
 * Grants or revokes a permission for a user. The server enforces the
 * delegation rule; this only reflects the granter's choice.
 *
 * @param userId - The user.
 * @param permission - The permission key.
 * @param grant - True to grant, false to revoke.
 */
export async function setUserPermission(
  userId: string,
  permission: string,
  grant: boolean,
): Promise<void> {
  const url = `/api/v3/users/${userId}/permissions/${permission}`;
  const headers = await authHeader();
  if (grant) {
    await axios.put(url, {}, { headers });
  } else {
    await axios.delete(url, { headers });
  }
}


/** Legacy attachment reference — relative path or absolute URL, any host. */
const LEGACY_ATTACHMENT = /\/api\/v1\/attachments\/[a-f0-9]{24}\b/i;

/** The pieces of a person needed to resolve their avatar. */
export interface AvatarOwner {
  readonly _id?: string | undefined;
  readonly avatar?: string | null | undefined;
}

/**
 * Resolves a person's stored avatar value to a loadable URL.
 *
 * New avatars are absolute S3 URLs and pass through. Legacy ones reference V1
 * attachments that no longer resolve anywhere public (the legacy host
 * requires auth) — those route to the V3 avatar endpoint keyed by the user.
 * Requesting it relative keeps the call same-origin, so the SPA's `/api`
 * proxy carries the Clerk session cookie where an `<img>` cannot attach an
 * Authorization header.
 *
 * @param owner - The person's id and stored avatar value.
 * @returns A loadable URL, or null when there is no usable avatar.
 */
export function avatarSrc(owner: AvatarOwner): string | null {
  const { _id, avatar } = owner;
  if (!avatar) return null;
  if (avatar.includes('/assets/icons/avatar/')) return null;
  // ?v busts caches poisoned by an early build that served cacheable errors.
  if (LEGACY_ATTACHMENT.test(avatar)) return _id ? `/api/v3/users/${_id}/avatar?v=2` : null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  return null;
}
